/** The Notifications grid search namespaces wired to their suggestion fetchers.
 *
 * Operators per namespace are a SUBSET of the spec's `x-namespace-operators`
 * on NotificationFilterNode (contract-tested). `exact` is deliberately omitted
 * wherever `iexact` exists (tenant/theme/language_policy/block/list) — the DSL
 * serialises both to `=`, which re-parses to `iexact`, so an `exact` chip
 * couldn't round-trip through the URL; ID pinning goes through the backend's
 * all-digits fallback where supported. `id` is exact-only (integer), which
 * round-trips fine.
 *
 * The `template` namespace free-text-searches every template-source column.
 * DTL fragments survive verbatim inside QUOTED values — the widget quotes
 * committed chips automatically; only hand-typed raw DSL needs manual quotes:
 * `template=~"{% if booking.state == 1 %}"`.
 */
import type { NamespaceConfigMap } from '@/components/QuerySearch';

import {
  fetchBlockListSuggestions,
  fetchBlockSuggestions,
  fetchEventCodeSuggestions,
  fetchPolicySuggestions,
  fetchThemeSuggestions,
  makeSuggestionFetcher,
  TOUR_OVERRIDE_MODE_LABELS,
} from '@/api/notifications';

// The 5-entry booking-state list (matches Group_Tour_User.BOOKING_STATES).
// NOT @/api/notifications' BOOKING_STATE_LABELS, which is a stale 3-entry map.
import { BOOKING_STATE_LABELS } from '../bookings/bookingsNamespaces';

export const RENDERING_LABELS = ['standard', 'custom', 'mixed'];

const METHOD_LABELS = [
  'Email',
  'SMS',
  'WhatsApp',
  'Airbnb',
  'Chat message',
  'Push notification',
  'Sentry alert',
  'SNS topic',
  'HTTP request',
];

/** Build a local (no-API) suggestion fetcher from a fixed label list. */
function localLabels(labels: string[]) {
  return (query: string) =>
    Promise.resolve(
      labels
        .filter((s) => s.toLowerCase().includes(query.toLowerCase()))
        .map((s) => ({ value: s, label: s })),
    );
}

export function buildNotificationNamespaces(): NamespaceConfigMap {
  return {
    tenant: {
      label: 'tenant',
      operators: ['iexact'],
      valueType: 'string',
      fetchSuggestions: makeSuggestionFetcher('tenant'),
      help: 'Tour operator (tenant) by code; an all-digits value also matches the tenant id. Use "system" for system templates.',
      examples: ['tenant=curatedplanet', 'tenant=system'],
    },
    is_system: {
      label: 'is_system',
      operators: ['iexact'],
      valueType: 'string',
      fetchSuggestions: localLabels(['yes', 'no']),
      help: 'System templates only (no tenant) — yes/no.',
      examples: ['is_system=yes'],
    },
    name: {
      label: 'name',
      operators: ['iexact', 'icontains'],
      valueType: 'string',
      help: 'Notification name.',
      examples: ['name=~welcome', 'name="Booking confirmed"'],
    },
    event: {
      label: 'event',
      operators: ['iexact', 'icontains'],
      valueType: 'string',
      fetchSuggestions: fetchEventCodeSuggestions,
      help: 'Event code that triggers the notification.',
      examples: ['event=tour_participant.created', 'event=~departure'],
    },
    // role has no dedicated autocomplete (role names are tenant-scoped free
    // text); tour is exact-by-id (integer, like `id`) — neither has a fetcher.
    role: {
      label: 'role',
      operators: ['iexact'],
      valueType: 'string',
      help: 'Targets guests/staff with this role (Employee_Role name).',
      examples: ['role=Guide', 'role="Lead guide"'],
    },
    tour: {
      label: 'tour',
      operators: ['exact'],
      valueType: 'integer',
      help: 'Scoped to this specific tour (Group_Tour id).',
      examples: ['tour=8123'],
    },
    booking_state: {
      label: 'booking_state',
      operators: ['iexact'],
      valueType: 'string',
      fetchSuggestions: localLabels(BOOKING_STATE_LABELS),
      help: 'Booking state the notification targets, by label or raw integer value.',
      examples: ['booking_state=Booked', 'booking_state="On hold"'],
    },
    override: {
      label: 'override',
      operators: ['iexact'],
      valueType: 'string',
      fetchSuggestions: localLabels(Object.values(TOUR_OVERRIDE_MODE_LABELS)),
      help: 'Tour override mode, by label or raw integer value.',
      examples: ['override="Send always"', 'override="Tour-specific override"'],
    },
    enabled: {
      label: 'enabled',
      operators: ['iexact'],
      valueType: 'string',
      fetchSuggestions: localLabels(['yes', 'no']),
      help: 'Whether the notification is live (yes/no).',
      examples: ['enabled=no'],
    },
    method: {
      label: 'method',
      operators: ['iexact'],
      valueType: 'string',
      fetchSuggestions: localLabels(METHOD_LABELS),
      help: 'Has a delivery method on this channel (by label or integer code).',
      examples: ['method=email', 'method=whatsapp'],
    },
    rendering: {
      label: 'rendering',
      operators: ['iexact'],
      valueType: 'string',
      fetchSuggestions: localLabels(RENDERING_LABELS),
      help: 'Delivery methods render via a standard template, custom source, or a mix.',
      examples: ['rendering=custom', 'rendering=mixed'],
    },
    theme: {
      label: 'theme',
      operators: ['iexact'],
      valueType: 'string',
      fetchSuggestions: fetchThemeSuggestions,
      help: 'A delivery method uses this theme (codename).',
      examples: ['theme=brand2026'],
    },
    language_policy: {
      label: 'language_policy',
      operators: ['iexact'],
      valueType: 'string',
      fetchSuggestions: fetchPolicySuggestions,
      help: 'The notification resolves its rendering language via this policy (codename).',
      examples: ['language_policy=guest_first'],
    },
    block: {
      label: 'block',
      operators: ['iexact'],
      valueType: 'string',
      fetchSuggestions: fetchBlockSuggestions,
      help: 'References this block — directly or through a referenced block list (codename).',
      examples: ['block=header', 'block=signature_html'],
    },
    list: {
      label: 'list',
      operators: ['iexact'],
      valueType: 'string',
      fetchSuggestions: fetchBlockListSuggestions,
      help: 'References this block list (codename).',
      examples: ['list=email_defaults'],
    },
    template: {
      label: 'template',
      operators: ['icontains'],
      valueType: 'string',
      help: 'Free-text search across all custom template sources (subjects + bodies). Quote fragments containing spaces or symbols.',
      examples: ['template=~"{% if booking.state == 1 %}"', 'template=~"block.header"'],
    },
    updated: {
      label: 'updated',
      operators: ['exact', 'range'],
      valueType: 'datetime',
      help: 'When the notification was last edited, UTC. `=` matches a whole day; a range takes date-time bounds.',
      examples: ['updated=2026-07-20', 'updated="2026-07-01 09:00".."2026-07-24 18:00"'],
    },
    created: {
      label: 'created',
      operators: ['exact', 'range'],
      valueType: 'datetime',
      help: 'When the notification was created, UTC. `=` matches a whole day; a range takes date-time bounds.',
      examples: ['created=2026-01-01..2026-07-01'],
    },
    id: {
      label: 'id',
      operators: ['exact'],
      valueType: 'integer',
      help: 'Notification id.',
      examples: ['id=5192'],
    },
  };
}
